import { useMutation, useQuery } from '@apollo/client';
import * as React from 'react';

import { AggregateStats, SearchCounts, SearchKeywordSets } from 'shared/models/search';

import {
    GET_AGENT_SEARCH_PROFILES,
    GET_SEARCH_DRAFT_METADATA,
    REFRESH_AGENT_SEARCH_PROFILES,
    SAVE_AGENT_SEARCH,
    SaveAgentSearchResult,
    SearchDraftMetadata,
    SearchProfilesResult
} from '../graphql/queries/agent';
import { useSnackbar } from './use-snackbar';

const DEFAULT_PAGE_SIZE = 10;

interface SearchResultsContextValue {
    aggregateStats?: AggregateStats | null;
    counts?: SearchCounts | null;
    keywordSets?: SearchKeywordSets | null;
    searchDraftId: string;
    searchId: string | undefined;
    jobId: string;
    userId: string | undefined;
    currentPage: number;
    error?: Error;
    handleGoToPage: (page: number) => void;
    handleRefresh: () => Promise<void>;
    handleSave: () => Promise<SaveAgentSearchResult>;
    loading: boolean;
    profiles: SearchProfilesResult['profiles'];
    refreshing: boolean;
    resultsExpired: boolean;
    saving: boolean;
    strategy?: string;
    title?: string;
    totalPages: number;
}

const SearchResultsContext = React.createContext<SearchResultsContextValue | null>(null);

interface SearchResultsProviderProps {
    children: React.ReactNode;
    searchDraftId: string;
}

export const SearchResultsProvider: React.FC<SearchResultsProviderProps> = ({ children, searchDraftId }) => {
    const [offset, setOffset] = React.useState(0);
    const { setSnackbar } = useSnackbar();

    // Fetch metadata (strategy, title, count) directly from table
    const {
        data: metadataData,
        loading: metadataLoading,
        refetch: refetchMetadata
    } = useQuery<{
        agent_drafts_searches_by_pk: SearchDraftMetadata | null;
    }>(GET_SEARCH_DRAFT_METADATA, {
        variables: { id: searchDraftId }
    });

    // Fetch profiles via action (queries BigQuery)
    const {
        data: profilesData,
        loading: profilesLoading,
        error,
        refetch
    } = useQuery<{ getAgentSearchProfiles: SearchProfilesResult }>(GET_AGENT_SEARCH_PROFILES, {
        fetchPolicy: 'cache-and-network',
        variables: { limit: DEFAULT_PAGE_SIZE, offset, searchDraftId }
    });

    const [refreshMutation, { loading: refreshing }] = useMutation<{
        refreshAgentSearchProfiles: SearchProfilesResult;
    }>(REFRESH_AGENT_SEARCH_PROFILES);

    const [saveMutation, { loading: saving }] = useMutation<{
        saveAgentSearch: SaveAgentSearchResult;
    }>(SAVE_AGENT_SEARCH);

    const metadata = metadataData?.agent_drafts_searches_by_pk;
    const profilesResult = profilesData?.getAgentSearchProfiles;

    const counts = metadata?.counts ?? undefined;
    const totalCount = counts?.total ?? 0;
    const strategy = metadata?.strategy;
    const title = metadata?.title;
    const aggregateStats = metadata?.aggregateStats ?? undefined;
    const keywordSets = metadata?.keywordSets ?? undefined;
    const profiles = profilesResult?.profiles ?? [];
    const resultsExpired = profilesResult?.error === 'RESULTS_EXPIRED';

    const loading = metadataLoading || profilesLoading;
    const totalPages = Math.ceil(totalCount / DEFAULT_PAGE_SIZE);
    const currentPage = Math.floor(offset / DEFAULT_PAGE_SIZE) + 1;

    const handleGoToPage = React.useCallback(
        (page: number) => {
            const maxPage = Math.max(1, totalPages);
            const targetPage = Math.max(1, Math.min(page, maxPage));
            setOffset((targetPage - 1) * DEFAULT_PAGE_SIZE);
        },
        [totalPages]
    );

    const handleRefresh = React.useCallback(async () => {
        try {
            const response = await refreshMutation({
                variables: { limit: DEFAULT_PAGE_SIZE, offset: 0, searchDraftId }
            });
            if (response.data?.refreshAgentSearchProfiles) {
                setOffset(0);
                refetch();
            }
        } catch (err) {
            setSnackbar('Failed to refresh search results');
        }
    }, [refreshMutation, refetch, searchDraftId, setSnackbar]);

    const handleSave = React.useCallback(async () => {
        try {
            const response = await saveMutation({ variables: { searchDraftId } });
            const result = response.data?.saveAgentSearch ?? { error: 'No response' };
            if (result.searchId) {
                refetchMetadata();
                setSnackbar('Search saved');
            } else {
                throw new Error(result.error || 'Unknown error');
            }
            return result;
        } catch (err) {
            setSnackbar('Error saving search');
            return { error: (err as Error).message };
        }
    }, [saveMutation, searchDraftId, refetchMetadata]);

    const value: SearchResultsContextValue = {
        aggregateStats,
        counts,
        currentPage,
        error,
        handleGoToPage,
        handleRefresh,
        handleSave,
        jobId: metadata?.jobId,
        keywordSets,
        loading,
        profiles,
        refreshing,
        resultsExpired,
        saving,
        searchDraftId,
        searchId: metadata?.searchId,
        strategy,
        title,
        totalPages,
        userId: metadata?.userId
    };

    return <SearchResultsContext.Provider value={value}>{children}</SearchResultsContext.Provider>;
};

export const useSearchResults = () => {
    const context = React.useContext(SearchResultsContext);
    if (!context) {
        throw new Error('useSearchResults must be used within SearchResultsProvider');
    }
    return context;
};
